import { useMemo, useState } from "react";
import { ChevronDown, AlertTriangle } from "lucide-react";
import * as Popover from "@radix-ui/react-popover";

import { useAvailableDatapoints } from "@/hooks/useAlarmRules";
import { cn } from "@/lib/cn";
import { formatRelativeAge } from "@/lib/format";
import type { DatapointInfo } from "@/data/alarmRules/wireFormat";

const CATEGORY_ORDER = ["battery", "smartmeter", "grid", "controller", "other"] as const;

interface Props {
  deviceId: string | null;
  value: string;
  onChange: (topic: string) => void;
  /** Filter the catalog to a single data type. */
  filterType?: "number" | "boolean" | "string";
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Combobox sourced from the device's `available_datapoints` SERVER_SCOPE
 * attribute. Falls back to a "custom topic" input when the operator
 * needs a topic the edge hasn't seen yet (we render a warning so they
 * know the rule will load but never fire until matching telemetry
 * lands).
 */
export function DatapointPicker({
  deviceId,
  value,
  onChange,
  filterType,
  disabled,
  placeholder,
}: Props) {
  const { data, isLoading } = useAvailableDatapoints(deviceId);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [customMode, setCustomMode] = useState(false);

  // When the edge hasn't published `available_datapoints` yet (older
  // firmware that predates the DatapointRegistry catalog), `data?.datapoints`
  // is empty. We surface this state explicitly with a non-blocking banner
  // so the operator knows why no topics appear, then they can switch to
  // the Custom-topic escape hatch without confusion.
  const catalogEmpty = !isLoading && (data?.datapoints?.length ?? 0) === 0;

  const filtered = useMemo(() => {
    const list = (data?.datapoints ?? []) as DatapointInfo[];
    const q = query.trim().toLowerCase();
    const byType = list.filter(
      (d) => !filterType || d.data_type === filterType,
    );
    if (!q) return byType;
    return byType.filter(
      (d) =>
        d.topic.toLowerCase().includes(q) ||
        (d.label ?? "").toLowerCase().includes(q) ||
        d.category.toLowerCase().includes(q),
    );
  }, [data, filterType, query]);

  const grouped = useMemo(() => {
    const buckets = new Map<string, DatapointInfo[]>();
    for (const d of filtered) {
      const cat = d.category || "other";
      const arr = buckets.get(cat) ?? [];
      arr.push(d);
      buckets.set(cat, arr);
    }
    const ordered: [string, DatapointInfo[]][] = [];
    for (const cat of CATEGORY_ORDER) {
      const arr = buckets.get(cat);
      if (arr) ordered.push([cat, arr]);
      buckets.delete(cat);
    }
    for (const [cat, arr] of buckets) ordered.push([cat, arr]);
    return ordered;
  }, [filtered]);

  const valueInfo = useMemo(
    () => (data?.datapoints ?? []).find((d) => d.topic === value) ?? null,
    [data, value],
  );

  const customTopicMatches = !valueInfo && !!value;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "w-full text-left form-input flex items-center gap-2",
            customTopicMatches && "border-warn/40",
          )}
        >
          <span className="flex-1 min-w-0 truncate font-mono text-[12px]">
            {value || (placeholder ?? "Pick a datapoint…")}
          </span>
          {customTopicMatches && (
            <AlertTriangle className="size-3.5 text-warn shrink-0" aria-label="Custom topic — never observed" />
          )}
          <ChevronDown className="size-3.5 text-text-faint shrink-0" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="z-50 w-[min(540px,calc(100vw-2rem))] max-h-[480px] overflow-hidden rounded-md border border-edge bg-surface shadow-xl flex flex-col"
        >
          <div className="border-b border-edge p-2 space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search topics, labels, categories…"
                className="form-input flex-1"
                autoFocus
              />
              <label className="flex items-center gap-1 text-[11px] text-text-faint whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={customMode}
                  onChange={(e) => setCustomMode(e.target.checked)}
                />
                Custom topic
              </label>
            </div>
            {customMode && (
              <div className="rounded border border-warn/40 bg-warn/10 text-warn text-[11px] p-2">
                <strong className="block">⚠ Custom topic — never observed.</strong>
                The rule will be loaded by the edge but won't fire
                until matching telemetry arrives. The edge will report
                "datapoint not observed yet" in the ACK errors.
                <input
                  type="text"
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  placeholder="battery/measurement/9/custom_value"
                  className="form-input w-full mt-2 font-mono text-[12px] text-text"
                />
              </div>
            )}
          </div>
          <div className="flex-1 overflow-auto">
            {isLoading && (
              <div className="p-4 text-text-faint text-[12px]">Loading…</div>
            )}
            {catalogEmpty && !customMode && (
              <div className="m-2 rounded border border-warn/40 bg-warn/10 text-warn text-[11px] p-2">
                <strong className="block">
                  This edge hasn't published a datapoint catalog yet.
                </strong>
                Either the edge runs an older firmware that predates the
                v3 DatapointRegistry, or no telemetry has been observed on
                the device. Toggle <em>Custom topic</em> above to author
                rules against a known topic name — the edge will subscribe
                to it on load and the rule fires once telemetry arrives.
              </div>
            )}
            {!isLoading && grouped.length === 0 && !customMode && !catalogEmpty && (
              <div className="p-4 text-text-faint text-[12px]">
                No datapoints match. Toggle "Custom topic" to enter one
                manually.
              </div>
            )}
            {grouped.map(([category, list]) => (
              <div key={category}>
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-text-faint bg-surface-2/60 border-b border-edge sticky top-0">
                  {category} ({list.length})
                </div>
                {list.map((d) => (
                  <button
                    type="button"
                    key={d.topic}
                    onClick={() => {
                      onChange(d.topic);
                      setOpen(false);
                    }}
                    className={cn(
                      "w-full text-left px-3 py-2 border-b border-edge/40 hover:bg-surface-2/60",
                      value === d.topic && "bg-accent/10",
                    )}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[12px] flex-1 min-w-0 truncate">
                        {d.topic}
                      </span>
                      <DataTypePill type={d.data_type} />
                      {d.unit && (
                        <span className="text-[10px] text-text-faint">
                          {d.unit}
                        </span>
                      )}
                      {d.stale && (
                        <span className="text-[10px] uppercase text-warn">
                          stale
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-text-faint mt-0.5">
                      {d.label && <span>{d.label}</span>}
                      <span>
                        last value: {String(d.last_value ?? "—")}
                      </span>
                      <span>
                        {/* last_seen_ts is an absolute epoch-ms stamp (edge
                            DatapointRegistry `last_seen_ms`); 0 = never seen. */}
                        seen {d.last_seen_ts ? formatRelativeAge(Date.now() - d.last_seen_ts) : "never"}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function DataTypePill({ type }: { type: DatapointInfo["data_type"] }) {
  const tone =
    type === "number"
      ? "border-accent/40 bg-accent/10 text-accent"
      : type === "boolean"
        ? "border-warn/40 bg-warn/10 text-warn"
        : type === "string"
          ? "border-edge bg-surface-2/40 text-text-faint"
          : "border-edge bg-surface-2/40 text-text-faint";
  return (
    <span
      className={cn(
        "text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-semibold",
        tone,
      )}
    >
      {type}
    </span>
  );
}
